import React from 'react';
import { Calculator, Category } from '../../types';
import { Icon } from '../Icon';

interface CalculatorShellProps {
  calculator: Calculator;
  onBack: () => void;
  badgeText?: string;
  badgeClassName?: string;
  iconWrapperClassName?: string;
  children: React.ReactNode;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  onExportPDF?: () => void;
  onShareWhatsApp?: () => void;
}

export const CalculatorShell: React.FC<CalculatorShellProps> = ({
  calculator,
  onBack,
  badgeText,
  badgeClassName,
  iconWrapperClassName,
  children,
  isFavorite,
  onToggleFavorite,
  onExportPDF,
  onShareWhatsApp,
}) => {
  const categoryTheme = {
    [Category.Financeiro]: {
      badgeText: 'Financeiro',
      badgeClassName: 'text-category-finance bg-category-finance',
      iconWrapperClassName: 'bg-category-finance text-category-finance',
    },
    [Category.Trabalhista]: {
      badgeText: 'Trabalhista',
      badgeClassName: 'text-category-work bg-category-work',
      iconWrapperClassName: 'bg-category-work text-category-work',
    },
    [Category.Simuladores]: {
      badgeText: 'Simulador',
      badgeClassName: 'text-category-sim bg-category-sim',
      iconWrapperClassName: 'bg-category-sim text-category-sim',
    },
    [Category.Tempo]: {
      badgeText: 'Tempo e Datas',
      badgeClassName: 'text-category-time bg-category-time',
      iconWrapperClassName: 'bg-category-time text-category-time',
    },
    [Category.Saude]: {
      badgeText: 'Saúde',
      badgeClassName: 'text-category-health bg-category-health',
      iconWrapperClassName: 'bg-category-health text-category-health',
    },
    [Category.Planejamento]: {
      badgeText: 'Planejamento',
      badgeClassName: 'text-category-plan bg-category-plan',
      iconWrapperClassName: 'bg-category-plan text-category-plan',
    },
    [Category.Consumo]: {
      badgeText: 'Consumo',
      badgeClassName: 'text-category-consumo bg-category-consumo',
      iconWrapperClassName: 'bg-category-consumo text-category-consumo',
    },
    [Category.Matematica]: {
      badgeText: 'Matemática',
      badgeClassName: 'text-category-math bg-category-math',
      iconWrapperClassName: 'bg-category-math text-category-math',
    },
    [Category.DevTools]: {
      badgeText: 'Dev Tools',
      badgeClassName: 'text-theme-text bg-theme-muted-section px-4',
      iconWrapperClassName: 'bg-theme-muted-section text-theme-text',
    },
  } as const;

  const theme = categoryTheme[calculator.category] ?? {
    badgeText: 'Calculadora',
    badgeClassName: 'text-theme-text bg-theme-muted-section',
    iconWrapperClassName: 'bg-theme-muted-section text-theme-text',
  };

  const resolvedBadgeText = badgeText ?? theme.badgeText;
  const resolvedBadgeClassName = badgeClassName ?? theme.badgeClassName;
  const resolvedIconWrapperClassName = iconWrapperClassName ?? theme.iconWrapperClassName;

  return (
    <div className="min-h-screen bg-theme-bg flex flex-col font-inter transition-colors duration-300">
      <nav className="bg-theme-card border-b border-theme-border px-6 py-4 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={onBack}
            className="group flex items-center gap-2.5 text-base font-bold text-theme-muted hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            <Icon
              name="arrow-left"
              className="w-5 h-5 transform group-hover:-translate-x-1.5 transition-transform"
            />
            Voltar ao Hub
          </button>

          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${resolvedIconWrapperClassName} dark:bg-slate-800`}>
              <Icon name={calculator.icon} className="w-5 h-5" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-black text-theme-text uppercase tracking-tight leading-none mb-1">{calculator.title}</span>
              <button
                onClick={onToggleFavorite}
                className={`flex items-center gap-2 group/fav px-3 py-1.5 rounded-xl transition-all ${isFavorite ? 'bg-amber-100 dark:bg-amber-900/30' : 'hover:bg-theme-muted-section dark:hover:bg-slate-800'}`}
              >
                <Icon
                  name="star"
                  className={`w-5 h-5 transition-all ${isFavorite ? 'fill-amber-500 text-amber-500 scale-110' : 'text-slate-300 dark:text-theme-muted group-hover/fav:text-amber-500 group-hover/fav:scale-110'}`}
                />
                <span className={`text-[11px] font-black uppercase tracking-wider transition-colors ${isFavorite ? 'text-amber-700 dark:text-amber-500' : 'text-theme-muted group-hover/fav:text-amber-600'}`}>
                  {isFavorite ? 'Favoritado' : 'Favoritar'}
                </span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {onExportPDF && (
              <button
                onClick={onExportPDF}
                className="group flex flex-col items-center justify-center w-14 h-14 bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-100 dark:border-indigo-800/30 text-indigo-600 dark:text-indigo-400 hover:bg-accent-gradient dark:hover:bg-accent-gradient hover:text-white dark:hover:text-white rounded-2xl transition-all shadow-sm"
                title="Exportar PDF"
              >
                <Icon name="download" className="w-6 h-6" />
                <span className="text-[9px] font-black uppercase mt-1 tracking-tighter">PDF</span>
              </button>
            )}
            {onShareWhatsApp && (
              <button
                onClick={onShareWhatsApp}
                className="group flex flex-col items-center justify-center w-14 h-14 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-100 dark:border-emerald-800/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 dark:hover:bg-emerald-600 hover:text-white dark:hover:text-white rounded-2xl transition-all shadow-sm"
                title="Compartilhar WhatsApp"
              >
                <Icon name="message-circle" className="w-6 h-6" />
                <span className="text-[9px] font-black uppercase mt-1 tracking-tighter">Whats</span>
              </button>
            )}
            <div className="hidden md:block ml-1">
              <span className={`text-[10px] font-black px-4 py-2 rounded-xl uppercase tracking-widest shadow-sm ${resolvedBadgeClassName} dark:bg-slate-800`}>
                {resolvedBadgeText}
              </span>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-grow max-w-6xl mx-auto w-full px-6 py-12">
        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-[1000] text-theme-text mb-3 tracking-tight">{calculator.title}</h1>
          <p className="text-theme-muted font-medium text-lg leading-relaxed max-w-3xl">{calculator.description}</p>
        </div>

        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">{children}</div>
      </main>

      <footer className="py-12 border-t border-theme-border text-center">
        <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.3em]">
          Calculadora PRO • Ferramentas Úteis v4.0
        </p>
      </footer>
    </div>
  );
};
